import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { kb } from "@/lib/api";

const PROF_PCT = { Expert: 100, Advanced: 75, Intermediate: 50, Emerging: 25 };
const PROF_CLS = { Expert: "pill-green", Advanced: "pill-blue", Intermediate: "pill-gray", Emerging: "pill-gray" };

// Legacy records may only have level/tier; derive an objective-ish display.
function display(s) {
  const prof = s.proficiency || s.tier || (s.level >= 90 ? "Expert" : s.level >= 75 ? "Advanced" : "Intermediate");
  const pct = s.level || PROF_PCT[prof] || 50;
  return { prof, pct };
}

export default function Skills() {
  const nav = useNavigate();
  const [skills, setSkills] = useState([]);
  const [certs, setCerts] = useState([]);
  const [naics, setNaics] = useState([]);
  const [clearances, setClearances] = useState([]);
  const [tax, setTax] = useState({});
  const [addOpen, setAddOpen] = useState(null); // 'skill' | 'cert' | 'naics' | 'import' | null
  const [draft, setDraft] = useState({});
  const [projects, setProjects] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const [s, c, n, cl, t] = await Promise.all([
      kb.skills(), kb.certifications(), kb.naics(), kb.clearances(), kb.taxonomy().catch(() => ({})),
    ]);
    setSkills(s); setCerts(c); setNaics(n); setClearances(cl); setTax(t);
  };
  useEffect(() => { load(); }, []);

  const openSkill = () => { setDraft({}); setProjects([]); setAddOpen("skill"); };
  const err = (e, fallback) => window.alert(e?.response?.data?.detail || fallback);

  const addSkill = async () => {
    if (!draft.name) return;
    try {
      await kb.addSkill({
        name: draft.name,
        category: draft.category || "",
        years_experience: Number(draft.years_experience) || 0,
        staff_count: Number(draft.staff_count) || 0,
        last_used_year: draft.last_used_year ? Number(draft.last_used_year) : null,
        keywords: (draft.keywords || "").split(",").map((k) => k.trim()).filter(Boolean),
        projects: projects.filter((p) => p.title),
      });
      setAddOpen(null); setDraft({}); setProjects([]);
      await load();
    } catch (e) { err(e, "Could not add skill (KB Manager / PM role required)"); }
  };
  const delSkill = async (id) => {
    if (!window.confirm("Remove this skill?")) return;
    try { await kb.delSkill(id); await load(); } catch (e) { err(e, "Delete failed"); }
  };
  const addCert = async () => {
    if (!draft.name) return;
    try {
      await kb.addCert({
        name: draft.name, type: draft.type || "corporate", holder: draft.holder || "Org",
        holders_count: Number(draft.holders_count) || 1, issuer: draft.issuer || "",
        expiry: draft.expiry, status: draft.status || "Active",
        verification_url: draft.verification_url || "",
      });
      setAddOpen(null); setDraft({}); await load();
    } catch (e) { err(e, "Could not add certification"); }
  };
  const delCert = async (id) => {
    if (!window.confirm("Remove this certification?")) return;
    try { await kb.delCert(id); await load(); } catch (e) { err(e, "Delete failed"); }
  };
  const addNaics = async () => {
    if (!draft.code) return;
    try { await kb.addNaics({ code: draft.code, label: draft.label || "" }); setAddOpen(null); setDraft({}); await load(); }
    catch (e) { err(e, "Could not add NAICS (KB Manager / PM role required)"); }
  };
  const delNaics = async (id) => {
    try { await kb.delNaics(id); await load(); } catch (e) { err(e, "Delete failed"); }
  };

  const onImportFile = async (file) => {
    if (!file) return;
    setBusy(true);
    try { setPreview(await kb.importPreview(file)); }
    catch (e) { err(e, "Could not parse workbook"); }
    finally { setBusy(false); }
  };
  const confirmImport = async () => {
    setBusy(true);
    try {
      const r = await kb.importConfirm({ skills: preview.skills, certifications: preview.certifications });
      window.alert(`Imported ${r.skills_added} skills and ${r.certifications_added} certifications.`);
      setAddOpen(null); setPreview(null); await load();
    } catch (e) { err(e, "Import failed"); }
    finally { setBusy(false); }
  };

  const STATUS_CLS = { Active: "pill-green", "Renew Soon": "pill-orange", "In Process": "pill-orange", Expired: "pill-red" };
  const ICON = { lock: "🔒", building: "🏢", tag: "🏷", globe: "🌐" };

  return (
    <div className="page" data-testid="page-skills">
      <div className="page-header">
        <div>
          <div className="page-title">Skills &amp; Certifications</div>
          <div className="page-subtitle">Objective capability profile · structured metrics used by AI for matching &amp; content (no subjective ratings)</div>
        </div>
        <div className="flex gap-8">
          <button className="btn btn-ghost btn-sm" onClick={() => nav("/onboarding")} data-testid="onboarding-btn">🧭 Onboarding wizard</button>
          <button className="btn btn-ghost btn-sm" onClick={() => { setPreview(null); setAddOpen("import"); }} data-testid="import-btn">⬆ Import from Excel</button>
          <button className="btn btn-primary btn-sm" onClick={() => { setAddOpen("cert"); setDraft({}); }} data-testid="add-cert-btn">+ Add Certification</button>
        </div>
      </div>

      <div className="grid-2">
        <div className="card">
          <div className="card-header">
            <span className="card-title">Core Competencies</span>
            <span className="pill pill-blue">{skills.length} Active</span>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, padding: "16px 20px" }}>
            {skills.map((s) => {
              const d = display(s);
              const projCount = (s.projects || []).length;
              return (
                <div key={s.id} data-testid={`skill-${s.id}`}>
                  <div className="flex items-center justify-between" style={{ marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 600 }}>{s.name}</span>
                    <span className="flex items-center gap-8">
                      <span className={`pill ${PROF_CLS[d.prof] || "pill-gray"}`}>{d.prof}</span>
                      <span onClick={() => delSkill(s.id)} title="Remove" style={{ cursor: "pointer", opacity: 0.5 }} data-testid={`del-skill-${s.id}`}>×</span>
                    </span>
                  </div>
                  <div style={{ fontSize: 11.5, color: "var(--text-secondary)", marginBottom: 6 }}>
                    {s.years_experience ? `${s.years_experience} yrs` : "—"} ·{" "}
                    {s.staff_count ? `${s.staff_count} staff` : "—"} ·{" "}
                    {projCount} project{projCount === 1 ? "" : "s"}
                    {s.category ? ` · ${s.category}` : ""}
                  </div>
                  <div className="progress-bar"><div className="progress-fill fill-blue" style={{ width: `${d.pct}%` }} /></div>
                </div>
              );
            })}
            <button className="btn btn-ghost btn-sm" onClick={openSkill} data-testid="add-skill-btn" style={{ alignSelf: "flex-start" }}>+ Add Skill</button>
          </div>
        </div>

        <div className="flex flex-col" style={{ gap: 16 }}>
          <div className="card">
            <div className="card-header">
              <span className="card-title">Active Certifications</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setAddOpen("cert"); setDraft({}); }}>+ Add</button>
            </div>
            <div style={{ padding: "12px 16px" }}>
              <table className="data-table">
                <thead><tr><th>Certification</th><th>Type</th><th>Holder</th><th>Expiry</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {certs.map((c) => (
                    <tr key={c.id} data-testid={`cert-${c.id}`}>
                      <td className="font-600">
                        {c.name}
                        {c.verification_url ? <a href={c.verification_url} target="_blank" rel="noreferrer" title="Verify" style={{ marginLeft: 6 }}>🔗</a> : null}
                      </td>
                      <td style={{ color: "var(--text-secondary)" }}>{c.type || "—"}</td>
                      <td style={{ color: "var(--text-secondary)" }}>{c.holder}</td>
                      <td>{c.expiry || "—"}</td>
                      <td><span className={`pill ${STATUS_CLS[c.status] || "pill-gray"}`}>{c.status}</span></td>
                      <td><span onClick={() => delCert(c.id)} title="Remove" style={{ cursor: "pointer", opacity: 0.5 }} data-testid={`del-cert-${c.id}`}>×</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <span className="card-title">NAICS Codes</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setAddOpen("naics"); setDraft({}); }} data-testid="add-naics-btn">+ Add</button>
            </div>
            <div style={{ padding: "14px 20px", display: "flex", flexWrap: "wrap", gap: 8 }}>
              {naics.map((n) => (
                <span key={n.id} className="pill pill-blue" style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {n.code}{n.label ? ` — ${n.label}` : ""}
                  <span onClick={() => delNaics(n.id)} title="Remove" style={{ cursor: "pointer", opacity: 0.6 }} data-testid={`del-naics-${n.id}`}>×</span>
                </span>
              ))}
              {naics.length === 0 && <span style={{ fontSize: 12, color: "var(--text-muted)" }}>No NAICS codes yet.</span>}
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header"><span className="card-title">Clearances &amp; Set-Asides</span></div>
        <div style={{ padding: "16px 20px", display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 16 }}>
          {clearances.map((c) => (
            <div key={c.id} style={{ textAlign: "center", padding: 16, borderRadius: 8, border: "1px solid var(--border)" }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{ICON[c.icon] || "🔒"}</div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>{c.name}</div>
              <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>{c.detail}</div>
              <span className={`pill ${STATUS_CLS[c.status] || "pill-gray"}`} style={{ marginTop: 8 }}>{c.status}</span>
            </div>
          ))}
        </div>
      </div>

      {addOpen && (
        <div className="modal-backdrop" onClick={() => setAddOpen(null)}>
          <div className="modal-card" onClick={(e) => e.stopPropagation()} data-testid="add-modal" style={{ maxWidth: addOpen === "import" ? 560 : 460 }}>
            {addOpen === "import" ? (
              <>
                <h3>Import from Excel</h3>
                <div className="modal-sub">Upload an .xlsx with a Skills sheet (Skill / Years / Staff / Category) and/or a Certifications sheet. You'll preview before committing.</div>
                <input type="file" accept=".xlsx,.xlsm" onChange={(e) => onImportFile(e.target.files[0])} data-testid="import-file" />
                {busy && <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 8 }}>Parsing…</div>}
                {preview && (
                  <div style={{ marginTop: 12, fontSize: 12.5 }}>
                    <div style={{ fontWeight: 600, marginBottom: 6 }}>Preview · {preview.skills.length} skills, {preview.certifications.length} certifications</div>
                    <ul style={{ maxHeight: 180, overflow: "auto", paddingLeft: 18, margin: 0 }}>
                      {preview.skills.map((s, i) => <li key={`s${i}`}>{s.name} — {s.years_experience} yrs · {s.staff_count} staff</li>)}
                      {preview.certifications.map((c, i) => <li key={`c${i}`}>📜 {c.name} ({c.holder})</li>)}
                    </ul>
                  </div>
                )}
                <div className="flex gap-8" style={{ marginTop: 18, justifyContent: "flex-end" }}>
                  <button className="btn btn-ghost btn-sm" onClick={() => { setAddOpen(null); setPreview(null); }}>Cancel</button>
                  <button className="btn btn-primary btn-sm" disabled={!preview || busy || (!preview.skills.length && !preview.certifications.length)} onClick={confirmImport} data-testid="import-confirm">
                    Import
                  </button>
                </div>
              </>
            ) : addOpen === "naics" ? (
              <>
                <h3>Add NAICS Code</h3>
                <div className="modal-sub">Add a NAICS code to the company profile</div>
                <label>NAICS Code</label>
                <input value={draft.code || ""} onChange={(e) => setDraft({ ...draft, code: e.target.value })} placeholder="541512" data-testid="naics-code" />
                <label>Label</label>
                <input value={draft.label || ""} onChange={(e) => setDraft({ ...draft, label: e.target.value })} placeholder="Computer Systems Design Services" data-testid="naics-label" />
                <ModalButtons onCancel={() => setAddOpen(null)} onSave={addNaics} />
              </>
            ) : addOpen === "skill" ? (
              <>
                <h3>Add Skill</h3>
                <div className="modal-sub">Capture objective metrics — proficiency is derived, never entered.</div>
                <label>Capability</label>
                <input list="skill-suggestions" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="modal-name" />
                <datalist id="skill-suggestions">{(tax.skill_suggestions || []).map((s) => <option key={s} value={s} />)}</datalist>
                <label>Category / NAICS family</label>
                <input value={draft.category || ""} onChange={(e) => setDraft({ ...draft, category: e.target.value })} placeholder="541512 — IT Systems Design" />
                <div className="grid-2" style={{ gap: 10 }}>
                  <div><label>Years of experience</label><input type="number" min="0" value={draft.years_experience || ""} onChange={(e) => setDraft({ ...draft, years_experience: e.target.value })} data-testid="skill-years" /></div>
                  <div><label>Staff with skill</label><input type="number" min="0" value={draft.staff_count || ""} onChange={(e) => setDraft({ ...draft, staff_count: e.target.value })} data-testid="skill-staff" /></div>
                </div>
                <label>Keywords (comma-separated)</label>
                <input value={draft.keywords || ""} onChange={(e) => setDraft({ ...draft, keywords: e.target.value })} placeholder="AWS, GovCloud, Terraform" />
                <label>Project history</label>
                {projects.map((p, i) => (
                  <div key={i} className="flex gap-8" style={{ marginBottom: 6 }}>
                    <input style={{ flex: 2 }} placeholder="Project / contract title" value={p.title} onChange={(e) => setProjects(projects.map((x, j) => j === i ? { ...x, title: e.target.value } : x))} />
                    <input style={{ width: 80 }} type="number" placeholder="Year" value={p.year || ""} onChange={(e) => setProjects(projects.map((x, j) => j === i ? { ...x, year: Number(e.target.value) || null } : x))} />
                    <button className="btn btn-ghost btn-sm" onClick={() => setProjects(projects.filter((_, j) => j !== i))}>×</button>
                  </div>
                ))}
                <button className="btn btn-ghost btn-sm" style={{ alignSelf: "flex-start" }} onClick={() => setProjects([...projects, { title: "", year: null }])}>+ Add project</button>
                <ModalButtons onCancel={() => setAddOpen(null)} onSave={addSkill} />
              </>
            ) : (
              <>
                <h3>Add Certification</h3>
                <div className="modal-sub">Track a certification or compliance package</div>
                <label>Name</label>
                <input list="cert-suggestions" value={draft.name || ""} onChange={(e) => setDraft({ ...draft, name: e.target.value })} data-testid="modal-name" />
                <datalist id="cert-suggestions">{[...(tax.certifications || []), ...(tax.compliance_frameworks || [])].map((s) => <option key={s} value={s} />)}</datalist>
                <div className="grid-2" style={{ gap: 10 }}>
                  <div><label>Type</label><select value={draft.type || "corporate"} onChange={(e) => setDraft({ ...draft, type: e.target.value })}><option value="corporate">Corporate</option><option value="individual">Individual</option></select></div>
                  <div><label>Holders</label><input type="number" min="1" value={draft.holders_count || ""} onChange={(e) => setDraft({ ...draft, holders_count: e.target.value })} /></div>
                </div>
                <label>Holder</label>
                <input value={draft.holder || ""} onChange={(e) => setDraft({ ...draft, holder: e.target.value })} placeholder="Org or N staff" />
                <label>Issuer</label>
                <input value={draft.issuer || ""} onChange={(e) => setDraft({ ...draft, issuer: e.target.value })} placeholder="Cyber AB, AWS, ISO…" />
                <div className="grid-2" style={{ gap: 10 }}>
                  <div><label>Expiry</label><input value={draft.expiry || ""} onChange={(e) => setDraft({ ...draft, expiry: e.target.value })} placeholder="Dec 2026" /></div>
                  <div><label>Status</label><select value={draft.status || "Active"} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>{(tax.cert_statuses || ["Active", "Renew Soon", "In Process", "Expired"]).map((s) => <option key={s}>{s}</option>)}</select></div>
                </div>
                <label>Verification URL (registry link)</label>
                <input value={draft.verification_url || ""} onChange={(e) => setDraft({ ...draft, verification_url: e.target.value })} placeholder="https://…" />
                <ModalButtons onCancel={() => setAddOpen(null)} onSave={addCert} />
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function ModalButtons({ onCancel, onSave }) {
  return (
    <div className="flex gap-8" style={{ marginTop: 18, justifyContent: "flex-end" }}>
      <button className="btn btn-ghost btn-sm" onClick={onCancel}>Cancel</button>
      <button className="btn btn-primary btn-sm" onClick={onSave} data-testid="modal-save">Save</button>
    </div>
  );
}
